import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { getBusinessPath } from "../routes/businessRoutes";
import { motion } from "framer-motion";
import Hero from "../components/common/Hero";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardTitle } from "../components/ui/card";
import MainLayout from "../layouts/MainLayout";
import placeholder from "../assets/images/placeholder.webp";
import LazyImage from "../components/ui/LazyImage";
import { FadeInUp, StaggerContainer, StaggerItem } from "../components/animations/AnimationComponents";
import { PageTransition } from "../components/animations/SpecialEffects";
import { GradientOrb } from "../components/animations/BackgroundEffects";
import { useI18n } from "../hooks/useI18n";
import { useBusinesses } from "../hooks/useBusinesses";
import { useInstitutionalOrganization } from "../hooks/useInstitutionalOrganization";

const Home: React.FC = () => {
  const { text, t } = useI18n();
  const { businesses } = useBusinesses();
  const institutional = useInstitutionalOrganization();

  useEffect(() => {
    document.title = "ApiNativos";
  }, []);

  if (!institutional) return null;

  return (
    <MainLayout>
      <PageTransition>
        <Hero
          title={institutional.name}
          subtitle={institutional.slogan}
          backgroundImage={placeholder}
          height="large"
        />

        <section className="relative overflow-hidden bg-background">
          <GradientOrb className="absolute right-10 top-20 -z-10" size="lg" color="gray" />
          <div className="container mx-auto flex min-h-[60vh] max-w-3xl flex-col items-center justify-center py-16 text-center">
            <FadeInUp delay={0.2}>
              <h2 className="mb-6 text-3xl font-bold">{t(text.home.welcome, { name: institutional.name })}</h2>
            </FadeInUp>
            <FadeInUp delay={0.4}>
              <p className="mb-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">{institutional.description}</p>
            </FadeInUp>
            <FadeInUp delay={0.6}>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button asChild className="text-white">
                  <Link to="/quienes-somos">{text.home.learnMore}</Link>
                </Button>
              </motion.div>
            </FadeInUp>
          </div>
        </section>

        <section className="relative overflow-hidden bg-surface-cool py-16">
          <GradientOrb className="absolute bottom-10 left-10 -z-10" size="md" color="gray" />
          <div className="container mx-auto">
            <FadeInUp>
              <h2 className="mb-12 text-center text-3xl font-semibold">{text.home.businessesTitle}</h2>
            </FadeInUp>

            <StaggerContainer className="grid grid-cols-1 items-center justify-center gap-6 md:grid-cols-2 lg:grid-cols-3">
              {businesses.map((business) => (
                <StaggerItem key={business.id}>
                  <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                    <Card className="group h-full overflow-hidden">
                      <div className="relative flex h-48 items-center justify-center overflow-hidden bg-white">
                        <LazyImage
                          src={business.logo}
                          alt={business.name}
                          className="h-full w-full object-contain"
                          width="100%"
                          height="100%"
                          objectFit="contain"
                        />
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100"
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                      <CardContent className="p-4">
                        <CardTitle className="mb-2">{business.name}</CardTitle>
                        <p className="mb-3 line-clamp-3 text-sm text-muted-foreground">{business.shortDescription}</p>
                        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                          <Button asChild variant="outline" className="w-full">
                            <Link to={getBusinessPath(business.id)}>{text.home.seeMore}</Link>
                          </Button>
                        </motion.div>
                      </CardContent>
                    </Card>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>

      </PageTransition>
    </MainLayout>
  );
};

export default Home;
